import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { CreditCard, ChevronDown } from "lucide-react";
const billingFormSchema = z.object({
  fullName: z.string().min(2, "Full name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
  streetAddress: z.string().min(5, "Street address must be at least 5 characters"),
  city: z.string().min(2, "City must be at least 2 characters"),
  state: z.string().min(2, "State must be at least 2 characters"),
  zipCode: z.string().min(5, "ZIP code must be at least 5 characters"),
  country: z.string().min(2, "Please select a country")
});
type BillingFormData = z.infer<typeof billingFormSchema>;
interface BillingFormProps {
  onSubmit?: (data: BillingFormData) => void;
  isLoading?: boolean;
}
export function BillingForm({
  onSubmit,
  isLoading = false
}: BillingFormProps) {
  const {
    toast
  } = useToast();
  const {
    user
  } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const form = useForm<BillingFormData>({
    resolver: zodResolver(billingFormSchema),
    defaultValues: {
      fullName: "",
      email: "",
      streetAddress: "",
      city: "",
      state: "",
      zipCode: "",
      country: ""
    }
  });

  // Load existing billing info if available
  useEffect(() => {
    const loadBillingInfo = async () => {
      if (!user) return;
      try {
        const {
          data: billingInfo,
          error
        } = await supabase.from('billing_info').select('*').eq('user_id', user.id).single();
        if (error && error.code !== 'PGRST116') {
          // PGRST116 means no rows found
          console.error('Error loading billing info:', error);
          return;
        }
        if (billingInfo) {
          form.reset({
            fullName: billingInfo.full_name || "",
            email: billingInfo.email || "",
            streetAddress: billingInfo.street_address || "",
            city: billingInfo.city || "",
            state: billingInfo.state || "",
            zipCode: billingInfo.zip_code || "",
            country: billingInfo.country || ""
          });
        }
      } catch (error) {
        console.error('Error loading billing info:', error);
      }
    };
    loadBillingInfo();
  }, [user, form]);
  const handleSubmit = async (data: BillingFormData) => {
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to save billing information.",
        variant: "destructive"
      });
      return;
    }
    try {
      // Save billing info to the dedicated billing_info table
      const billingData = {
        user_id: user.id,
        full_name: data.fullName,
        email: data.email,
        street_address: data.streetAddress,
        city: data.city,
        state: data.state,
        zip_code: data.zipCode,
        country: data.country
      };

      // Use upsert to insert or update billing info
      const {
        error
      } = await supabase.from('billing_info').upsert(billingData, {
        onConflict: 'user_id'
      });
      if (error) {
        throw error;
      }
      console.log("Billing form submitted successfully:", data);
      toast({
        title: "User information saved",
        description: "Your user information has been updated successfully."
      });
      onSubmit?.(data);
    } catch (error) {
      console.error("Error saving billing info:", error);
      toast({
        title: "Error",
        description: "Failed to save user information. Please try again.",
        variant: "destructive"
      });
    }
  };
  return <Collapsible open={isOpen} onOpenChange={setIsOpen}>
    <Card>
      <CollapsibleTrigger asChild>
        <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                User Information
              </CardTitle>
              <CardDescription>
                Enter your user information for account management
              </CardDescription>
            </div>
            <ChevronDown className={`h-5 w-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </div>
        </CardHeader>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField control={form.control} name="fullName" render={({
            field
          }) => <FormItem>
                  <FormLabel>Full Name</FormLabel>
                  <FormControl>
                    <Input placeholder="John Doe" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={form.control} name="streetAddress" render={({
            field
          }) => <FormItem>
                  <FormLabel>Street Address</FormLabel>
                  <FormControl>
                    <Input placeholder="123 Main Street" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField control={form.control} name="city" render={({
              field
            }) => <FormItem>
                    <FormLabel>City</FormLabel>
                    <FormControl>
                      <Input placeholder="New York" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              
              <FormField control={form.control} name="state" render={({
              field
            }) => <FormItem>
                    <FormLabel>State</FormLabel>
                    <FormControl>
                      <Input placeholder="NY" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              
              <FormField control={form.control} name="zipCode" render={({
              field
            }) => <FormItem>
                    <FormLabel>ZIP Code</FormLabel>
                    <FormControl>
                      <Input placeholder="10001" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>

            <FormField control={form.control} name="country" render={({
            field
          }) => <FormItem>
                  <FormLabel>Country</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a country" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="US">United States</SelectItem>
                      <SelectItem value="CA">Canada</SelectItem>
                      <SelectItem value="GB">United Kingdom</SelectItem>
                      <SelectItem value="AU">Australia</SelectItem>
                      <SelectItem value="DE">Germany</SelectItem>
                      <SelectItem value="FR">France</SelectItem>
                      <SelectItem value="IT">Italy</SelectItem>
                      <SelectItem value="ES">Spain</SelectItem>
                      <SelectItem value="NL">Netherlands</SelectItem>
                      <SelectItem value="SE">Sweden</SelectItem>
                      <SelectItem value="NO">Norway</SelectItem>
                      <SelectItem value="DK">Denmark</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>} />

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? "Saving..." : "Save User Information"}
            </Button>
          </form>
        </Form>
        </CardContent>
      </CollapsibleContent>
    </Card>
  </Collapsible>;
}